<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { motion, useAnimate } from 'motion-v';
import type { Options } from 'motion-v';

const context = inject('context') as any;
const { width } = useWindowSize();
const tagRef = ref<HTMLElement | null>(null);
const nodeIndex = ref<number>(0);
const [scope, animate] = useAnimate();

// Register this tag and get its index
onMounted(() => {
    if (context && tagRef.value) {
        nodeIndex.value = context.registerTag(tagRef.value);
    }
});

// Get position for this specific node
const target = computed(() => {
    if (window.innerWidth <= 1023) {
        return { x: 0, y: 0 };
    }

    const position = context?.getNodePosition(nodeIndex.value);

    if (!position) {
        return { x: 0, y: 0 };
    }

    return position.target;
});

const springOptions: Options['transition'] = {
    type: 'spring',
    mass: 4,
    stiffness: 120,
    damping: 30,
};

const animateLines = () => {
    animate(context.getProgress(nodeIndex.value), [0, 100], {
        ...springOptions,
        delay: 0.25 + nodeIndex.value * 0.05,
    });
};
</script>

<template>
    <motion.div
        v-if="width > 1024"
        :initial="{
            x: '-50%',
            y: '-50%',
            scale: 0.1,
            opacity: 0,
        }"
        :whileInView="{
            x: `calc(${target.x}px - 50%)`,
            y: `calc(${target.y}px - 1.4375em)`,
            scale: 1,
            opacity: 1,
            transition: {
                default: {
                    ...springOptions,
                    delay: 0.25 + nodeIndex * 0.05,
                },
                scale: {
                    ease: 'easeInOut',
                    duration: 0.8,
                    delay: 0.25 + nodeIndex * 0.05,
                },
                opacity: { ease: 'easeOut', duration: 0.2, delay: 0.25 },
            },
        }"
        :inViewOptions="{
            margin: '20% 0% 10% 0%',
        }"
        @viewportEnter="animateLines"
        ref="tagRef"
        class="absolute top-1/2 left-1/2 has-[[data-state=open]]:z-20"
    >
        <slot />
    </motion.div>
    <div
        v-else
        class="my-5"
    >
        <slot />
    </div>
</template>
